import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { WC_GROUP_JOIN_APP_PATH } from '../../signIn/helpers/postAuthRedirect.helper';
import { WORLD_CUP_COMPETITION_ID } from '../../worldcup/worldcup.constants';
import {
  PENDING_WC_SQUAD_JOIN_SESSION_KEY,
  PENDING_WC_SQUAD_JOIN_STORAGE_KEY,
  PENDING_WC_SQUAD_JOIN_TTL_MS,
  WC_JOIN_OAUTH_PARAM,
  WC_JOIN_COMPETITION_OAUTH_PARAM,
} from '../constants/pendingWcSquadJoin.constants';

interface PendingWcSquadJoinPayload {
  inviteCode: string;
  savedAt: number;
  /**
   * Which competition the squad belongs to. Optional: payloads written before
   * Slice 3E (and any mid-OAuth right now) have no id and are World Cup joins,
   * so they must keep working across the deploy.
   */
  competitionId?: string;
}

/** Strip non-alphanumeric (e.g. hyphens) and uppercase — matches the server's normalization. */
function normalizeWcInviteCode(code: string): string {
  return code.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
}

function isValidWcInviteCode(code: string): boolean {
  const normalized = normalizeWcInviteCode(code);
  return normalized.length >= 4 && normalized.length <= 32;
}

function parsePayload(raw: string | null): PendingWcSquadJoinPayload | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (
      parsed &&
      typeof parsed === 'object' &&
      'inviteCode' in parsed &&
      'savedAt' in parsed &&
      typeof (parsed as PendingWcSquadJoinPayload).inviteCode === 'string' &&
      typeof (parsed as PendingWcSquadJoinPayload).savedAt === 'number'
    ) {
      const payload = parsed as PendingWcSquadJoinPayload;
      if (!isValidWcInviteCode(payload.inviteCode)) return null;
      if (Date.now() - payload.savedAt > PENDING_WC_SQUAD_JOIN_TTL_MS) return null;
      return {
        inviteCode: normalizeWcInviteCode(payload.inviteCode),
        savedAt: payload.savedAt,
        competitionId:
          typeof payload.competitionId === 'string' && payload.competitionId.trim()
            ? payload.competitionId.trim()
            : WORLD_CUP_COMPETITION_ID,
      };
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * `/app/worldcup/groups/join?code=CODE&autojoin=1` only. Returns the normalized
 * invite code when the path carries a deferred auto-join intent, else null.
 * A bare prefill link (no `autojoin=1`) is intentionally ignored — those show the
 * ticket and wait for an explicit tap rather than joining silently.
 */
export function extractWcInviteCodeFromReturnPath(path: string): string | null {
  return extractSquadJoinFromReturnPath(path)?.inviteCode ?? null;
}

/** Matches both '/app/worldcup/groups/join?…' and '/app/competition/<id>/groups/join?…'. */
export function extractSquadJoinFromReturnPath(
  path: string,
): { competitionId: string; inviteCode: string } | null {
  const trimmed = path.trim();
  const q = trimmed.indexOf('?');
  if (q === -1) return null;
  const pathname = trimmed.slice(0, q);

  let competitionId: string;
  if (pathname === WC_GROUP_JOIN_APP_PATH) {
    competitionId = WORLD_CUP_COMPETITION_ID;
  } else {
    const m = pathname.match(/^\/app\/competition\/([^/]+)\/groups\/join$/);
    if (!m) return null;
    competitionId = decodeURIComponent(m[1]);
  }
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(trimmed.slice(q + 1));
  } catch {
    return null;
  }
  if (params.get('autojoin') !== '1') return null;
  const raw = params.get('code');
  if (!raw || !isValidWcInviteCode(raw)) return null;
  return { competitionId, inviteCode: normalizeWcInviteCode(raw) };
}

export async function savePendingWcSquadJoinCode(
  inviteCodeRaw: string,
  competitionId: string = WORLD_CUP_COMPETITION_ID,
): Promise<void> {
  if (!isValidWcInviteCode(inviteCodeRaw)) return;
  const payload: PendingWcSquadJoinPayload = {
    inviteCode: normalizeWcInviteCode(inviteCodeRaw),
    savedAt: Date.now(),
    competitionId,
  };
  const serialized = JSON.stringify(payload);
  await AsyncStorage.setItem(PENDING_WC_SQUAD_JOIN_STORAGE_KEY, serialized);
  if (Platform.OS === 'web' && typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.setItem(PENDING_WC_SQUAD_JOIN_SESSION_KEY, serialized);
    } catch {
      // ignore quota / privacy mode
    }
  }
}

/**
 * Persist the WC squad-join intent before an OAuth redirect so it survives the
 * round-trip even when the sessionStorage-only `returnUrl` is lost — e.g. the
 * invite link was opened in an in-app browser (WhatsApp) and Google OAuth hands
 * off to a different browser context. No-op for any other returnUrl.
 */
export async function savePendingWcSquadJoinFromReturnPath(returnPath: string): Promise<void> {
  const parsed = extractSquadJoinFromReturnPath(returnPath);
  if (!parsed) return;
  await savePendingWcSquadJoinCode(parsed.inviteCode, parsed.competitionId);
}

export async function getPendingWcSquadJoinCode(): Promise<string | null> {
  let payload = parsePayload(await AsyncStorage.getItem(PENDING_WC_SQUAD_JOIN_STORAGE_KEY));
  if (!payload && Platform.OS === 'web' && typeof sessionStorage !== 'undefined') {
    try {
      payload = parsePayload(sessionStorage.getItem(PENDING_WC_SQUAD_JOIN_SESSION_KEY));
      if (payload) {
        await AsyncStorage.setItem(PENDING_WC_SQUAD_JOIN_STORAGE_KEY, JSON.stringify(payload));
      }
    } catch {
      // ignore
    }
  }
  return payload?.inviteCode ?? null;
}

/** Pending join incl. its competition (missing id ⇒ World Cup, for legacy payloads). */
export async function getPendingSquadJoin(): Promise<{ competitionId: string; inviteCode: string } | null> {
  let payload = parsePayload(await AsyncStorage.getItem(PENDING_WC_SQUAD_JOIN_STORAGE_KEY));
  if (!payload && Platform.OS === 'web' && typeof sessionStorage !== 'undefined') {
    try {
      payload = parsePayload(sessionStorage.getItem(PENDING_WC_SQUAD_JOIN_SESSION_KEY));
    } catch {
      // ignore
    }
  }
  if (!payload) return null;
  return {
    competitionId: payload.competitionId ?? WORLD_CUP_COMPETITION_ID,
    inviteCode: payload.inviteCode,
  };
}

/**
 * Append the WC squad-join code to the OAuth `redirectTo` URL so the intent rides the
 * redirect chain across a cross-app handoff (in-app browser → system Safari), where
 * sessionStorage AND localStorage are in a different browser and lost. Web only —
 * native completes OAuth in-context (openAuthSessionAsync) and keeps its AsyncStorage
 * intent, so it needs no URL carrier. Returns `redirectTo` unchanged when there is no
 * `autojoin=1` intent, keeping normal sign-ins' redirect clean (so the Supabase redirect
 * allowlist only ever sees a query param on genuine invite flows).
 */
export function appendWcJoinToOAuthRedirect(
  redirectTo: string | undefined,
  returnPath: string,
): string | undefined {
  if (Platform.OS !== 'web' || !redirectTo) return redirectTo;
  const parsed = extractSquadJoinFromReturnPath(returnPath);
  if (!parsed) return redirectTo;
  const sep = redirectTo.includes('?') ? '&' : '?';
  let next = `${redirectTo}${sep}${WC_JOIN_OAUTH_PARAM}=${encodeURIComponent(parsed.inviteCode)}`;
  // World Cup is the implied default on the read side, so only a non-WC join needs
  // the extra param — the WC redirect URL stays exactly what it was before Slice 3E.
  if (parsed.competitionId !== WORLD_CUP_COMPETITION_ID) {
    next += `&${WC_JOIN_COMPETITION_OAUTH_PARAM}=${encodeURIComponent(parsed.competitionId)}`;
  }
  return next;
}

/**
 * Read + validate a WC squad-join code carried on the OAuth callback URL's query string
 * (`/oauth-native-callback?wc_join=CODE`). Returns the normalized code, or null when the
 * param is absent or malformed. Accepts a leading `?` or a bare query string.
 */
export function extractWcJoinCodeFromCallbackSearch(search: string): string | null {
  if (!search) return null;
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  } catch {
    return null;
  }
  const raw = params.get(WC_JOIN_OAUTH_PARAM);
  if (!raw || !isValidWcInviteCode(raw)) return null;
  return normalizeWcInviteCode(raw);
}

/**
 * Same carrier as `extractWcJoinCodeFromCallbackSearch`, but also recovers WHICH
 * competition the code belongs to. A callback URL without the competition param is a
 * World Cup join — that covers both links emitted before this param existed and any
 * OAuth round-trip already in flight across the deploy.
 */
export function extractSquadJoinFromCallbackSearch(
  search: string,
): { competitionId: string; inviteCode: string } | null {
  const inviteCode = extractWcJoinCodeFromCallbackSearch(search);
  if (!inviteCode) return null;
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  } catch {
    return { competitionId: WORLD_CUP_COMPETITION_ID, inviteCode };
  }
  const rawCompetition = params.get(WC_JOIN_COMPETITION_OAUTH_PARAM)?.trim();
  return {
    competitionId: rawCompetition || WORLD_CUP_COMPETITION_ID,
    inviteCode,
  };
}

export async function clearPendingWcSquadJoinCode(): Promise<void> {
  await AsyncStorage.removeItem(PENDING_WC_SQUAD_JOIN_STORAGE_KEY);
  if (Platform.OS === 'web' && typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.removeItem(PENDING_WC_SQUAD_JOIN_SESSION_KEY);
    } catch {
      // ignore
    }
  }
}
